import {
    QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { AxiosError } from "axios";
import { closeTemplateContract } from "@common/contracts/close-template.contract";
import {
    CloseTemplate,
    CloseTemplateSchema,
    CloseTemplateWithStats,
    CloseTemplateWithStatsSchema,
} from "@common/types/closeTemplate";
import { CloseTemplateTypeFilter } from "@common/schemas/close-template.schema";
import { CloseTemplatesSortValue } from "@common/types/views/closeTemplatesView";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { workspaceQueryKeys } from "@api/workspace.api";
import { closeQueryKeys } from "@api/close.api";
import { invalidateAllTaskQueries } from "@api/taskQueryKeys";
import { organizationTaskTemplateQueryKeys } from "@api/organizationTaskTemplate.api";

const closeTemplateClient = getBackendAPIClient(closeTemplateContract);

const closeTemplateQueryKeys = {
    all: () => ["close-template"] as const,
    organization: (organizationId: string) =>
        [
            ...closeTemplateQueryKeys.all(),
            "organization",
            organizationId,
        ] as const,
    list: (
        organizationId: string,
        filters?: {
            serviceIds?: string[];
            ownerIds?: number[];
            noTier?: boolean;
            includeArchived?: boolean;
            typeFilter?: CloseTemplateTypeFilter;
            sourceTaskTemplateIds?: string[];
            sort?: CloseTemplatesSortValue;
        },
    ) =>
        [
            ...closeTemplateQueryKeys.organization(organizationId),
            "list",
            filters,
        ] as const,
    preview: (
        organizationId: string,
        closeTemplateId: string,
        body: ServerInferRequest<
            typeof closeTemplateContract.previewCloseTemplate
        >["body"],
    ) =>
        [
            ...closeTemplateQueryKeys.organization(organizationId),
            "preview",
            closeTemplateId,
            body,
        ] as const,
    resolveTaskView: (
        organizationId: string,
        body: ServerInferRequest<
            typeof closeTemplateContract.resolveTaskView
        >["body"],
    ) =>
        [
            ...closeTemplateQueryKeys.organization(organizationId),
            "resolve-task-view",
            body,
        ] as const,
};

const invalidateOrganizationCloseTemplates = ({
    queryClient,
    organizationId,
}: {
    queryClient: QueryClient;
    organizationId: string;
}) => {
    void queryClient.invalidateQueries({
        queryKey: closeTemplateQueryKeys.organization(organizationId),
    });
    void queryClient.invalidateQueries({
        queryKey:
            organizationTaskTemplateQueryKeys.organization(organizationId),
    });
};

interface UseCloseTemplatesOptions {
    serviceIds?: string[];
    ownerIds?: number[];
    noTier?: boolean;
    includeArchived?: boolean;
    typeFilter?: CloseTemplateTypeFilter;
    sourceTaskTemplateIds?: string[];
    sort?: CloseTemplatesSortValue;
    enabled?: boolean;
    /** React Query's `refetchOnMount`; false reuses cached templates when a reader mounts, even stale ones. */
    refetchOnMount?: boolean;
}

export function useCloseTemplates(options?: UseCloseTemplatesOptions) {
    const { organizationId } = useScope();
    const serviceIds = options?.serviceIds;
    const ownerIds = options?.ownerIds;
    const noTier = options?.noTier;
    const includeArchived = options?.includeArchived;
    const typeFilter = options?.typeFilter;
    const sourceTaskTemplateIds = options?.sourceTaskTemplateIds;
    const sort = options?.sort;
    const isEnabled = options?.enabled ?? true;

    return useQuery({
        ...(options?.refetchOnMount !== undefined && {
            refetchOnMount: options.refetchOnMount,
        }),
        queryKey: organizationId
            ? closeTemplateQueryKeys.list(organizationId, {
                  serviceIds,
                  ownerIds,
                  noTier,
                  includeArchived,
                  typeFilter,
                  sourceTaskTemplateIds,
                  sort,
              })
            : [],
        queryFn: async () => {
            if (!organizationId) {
                return [] as CloseTemplateWithStats[];
            }

            const response = await closeTemplateClient.listCloseTemplates({
                params: { organizationId },
                query: {
                    serviceIds: serviceIds?.length ? serviceIds : undefined,
                    ownerIds: ownerIds?.length ? ownerIds : undefined,
                    noTier: noTier || undefined,
                    includeArchived: includeArchived || undefined,
                    typeFilter: typeFilter ?? undefined,
                    sourceTaskTemplateIds: sourceTaskTemplateIds?.length
                        ? sourceTaskTemplateIds
                        : undefined,
                    sort: sort ?? undefined,
                },
            });

            return response.body.closeTemplates.map((t) =>
                CloseTemplateWithStatsSchema.parse(t),
            );
        },
        enabled: !!organizationId && isEnabled,
    });
}

export function useCreateCloseTemplateMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof closeTemplateContract.createCloseTemplate
            >["body"],
        ) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }

            const response = await closeTemplateClient.createCloseTemplate({
                params: { organizationId },
                body,
            });

            return CloseTemplateSchema.parse(response.body);
        },
        onSuccess: () => {
            if (!organizationId) {
                return;
            }
            invalidateOrganizationCloseTemplates({
                queryClient,
                organizationId,
            });
        },
    });
}

/** Thrown when the backend rejects a close template update with a 422 (e.g. accrual content exists). */
export class CloseTemplateUpdateValidationError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "CloseTemplateUpdateValidationError";
    }
}

export function useUpdateCloseTemplateMutation(
    closeTemplate?: Pick<CloseTemplate, "id">,
) {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof closeTemplateContract.updateCloseTemplate
            >["body"],
        ) => {
            if (!organizationId || !closeTemplate) {
                throw new Error("Missing context for close template update");
            }

            try {
                const response = await closeTemplateClient.updateCloseTemplate({
                    params: {
                        organizationId,
                        closeTemplateId: closeTemplate.id,
                    },
                    body,
                    // the 422 validation error is surfaced inline on the form
                    extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                });
                return CloseTemplateSchema.parse(response.body);
            } catch (e) {
                if (
                    e instanceof AxiosError &&
                    e.response?.status === 422 &&
                    typeof e.response?.data === "object" &&
                    e.response.data !== null &&
                    "message" in e.response.data &&
                    typeof e.response.data.message === "string"
                ) {
                    throw new CloseTemplateUpdateValidationError(
                        e.response.data.message,
                    );
                }
                throw e;
            }
        },
        onSuccess: () => {
            if (!organizationId) {
                return;
            }
            invalidateOrganizationCloseTemplates({
                queryClient,
                organizationId,
            });
        },
    });
}

export function useApplyCloseTemplateMutation(closeTemplateId?: string) {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof closeTemplateContract.applyCloseTemplate
            >["body"],
        ) => {
            if (!organizationId || !closeTemplateId) {
                throw new Error("Missing context");
            }
            const response = await closeTemplateClient.applyCloseTemplate({
                params: { organizationId, closeTemplateId },
                body,
            });
            if (response.status !== 201) {
                const errorBody = response.body;
                const errorMessage =
                    "message" in errorBody &&
                    typeof errorBody.message === "string"
                        ? errorBody.message
                        : "Failed to apply template";
                throw new Error(errorMessage);
            }
            return response.body;
        },
        onSuccess: () => {
            if (!organizationId) {
                return;
            }
            void queryClient.invalidateQueries({
                queryKey: closeTemplateQueryKeys.organization(organizationId),
            });
            void invalidateAllTaskQueries(queryClient);
            // Invalidate all org work-tab queries so navigating to the Work
            // page after template application shows the freshly-created closes
            // and tasks instead of stale data.
            void queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });
            void queryClient.invalidateQueries({
                queryKey: closeQueryKeys.organization(organizationId),
            });
        },
    });
}

export function usePreviewCloseTemplateQuery(
    closeTemplateId: string | null,
    body:
        | ServerInferRequest<
              typeof closeTemplateContract.previewCloseTemplate
          >["body"]
        | null,
) {
    const { organizationId } = useScope();

    return useQuery({
        queryKey:
            organizationId && closeTemplateId && body
                ? closeTemplateQueryKeys.preview(
                      organizationId,
                      closeTemplateId,
                      body,
                  )
                : [],
        queryFn: async () => {
            if (!organizationId || !closeTemplateId || !body) {
                throw new Error("Missing preview context");
            }
            const response = await closeTemplateClient.previewCloseTemplate({
                params: { organizationId, closeTemplateId },
                body,
            });
            if (response.status !== 200) {
                const errorBody = response.body;
                const errorMessage =
                    "message" in errorBody &&
                    typeof errorBody.message === "string"
                        ? errorBody.message
                        : "Failed to preview template";
                throw new Error(errorMessage);
            }
            return response.body;
        },
        enabled: !!organizationId && !!closeTemplateId && !!body,
    });
}

export function useDuplicateCloseTemplateMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (closeTemplateId: string) => {
            if (!organizationId) {
                throw new Error("No active organization");
            }

            const response = await closeTemplateClient.duplicateCloseTemplate({
                params: { organizationId, closeTemplateId },
            });

            return CloseTemplateSchema.parse(response.body);
        },
        onSettled: () => {
            if (!organizationId) {
                return;
            }
            invalidateOrganizationCloseTemplates({
                queryClient,
                organizationId,
            });
        },
    });
}

export function useDeleteCloseTemplateMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (closeTemplateId: string) => {
            if (!organizationId) {
                throw new Error("Missing organizationId");
            }

            const response = await closeTemplateClient.deleteCloseTemplate({
                params: {
                    organizationId,
                    closeTemplateId,
                },
            });

            if (response.status !== 200) {
                throw new Error("Failed to delete close template");
            }

            return response.body;
        },
        onSuccess: () => {
            if (!organizationId) {
                return;
            }
            invalidateOrganizationCloseTemplates({
                queryClient,
                organizationId,
            });
        },
    });
}

export function useArchiveCloseTemplateMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (closeTemplateId: string) => {
            if (!organizationId) {
                throw new Error("Missing organizationId");
            }
            const response = await closeTemplateClient.archiveCloseTemplate({
                params: { organizationId, closeTemplateId },
            });
            if (response.status !== 200) {
                throw new Error("Failed to archive close template");
            }
            return response.body;
        },
        onSuccess: () => {
            if (!organizationId) {
                return;
            }
            invalidateOrganizationCloseTemplates({
                queryClient,
                organizationId,
            });
        },
    });
}

export function useUnarchiveCloseTemplateMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (closeTemplateId: string) => {
            if (!organizationId) {
                throw new Error("Missing organizationId");
            }
            const response = await closeTemplateClient.unarchiveCloseTemplate({
                params: { organizationId, closeTemplateId },
            });
            if (response.status !== 200) {
                throw new Error("Failed to activate close template");
            }
            return response.body;
        },
        onSuccess: () => {
            if (!organizationId) {
                return;
            }
            invalidateOrganizationCloseTemplates({
                queryClient,
                organizationId,
            });
        },
    });
}

export function useResolveTaskViewQuery(
    body:
        | ServerInferRequest<
              typeof closeTemplateContract.resolveTaskView
          >["body"]
        | null,
) {
    const { organizationId } = useScope();

    return useQuery({
        queryKey:
            organizationId && body
                ? closeTemplateQueryKeys.resolveTaskView(organizationId, body)
                : [],
        queryFn: async () => {
            if (!organizationId || !body) {
                throw new Error("Missing task view context");
            }
            const response = await closeTemplateClient.resolveTaskView({
                params: { organizationId },
                body,
                // a draft view config can be momentarily invalid; the preview
                // explains it inline instead of toasting on every keystroke
                extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
            });
            if (response.status !== 200) {
                const errorBody = response.body;
                throw new Error(
                    "message" in errorBody &&
                        typeof errorBody.message === "string"
                        ? errorBody.message
                        : "Failed to resolve task view",
                );
            }
            return response.body;
        },
        enabled: !!organizationId && !!body,
        retry: false,
    });
}
